import {useState} from 'react';
import {commitTicketingMutation} from '../../../graphql/forecastRelay';
import CreateTicketMutation from '../../../../../mutations/ticketing/tickets/createTicketMutation';
import type {TicketChannel, TicketPriority, TicketStatus} from '../types/ticket';

export interface CreateTicketInput {
	subject: string;
	description?: string;
	priority: TicketPriority;
	status?: TicketStatus;
	// Records which surface created the ticket. Omitting it lets the backend
	// default to "api", which is only correct for programmatic callers.
	channel?: TicketChannel;
	requesterId?: string;
	requesterEmail?: string;
	assigneeId?: string;
	labels?: string[];
	// A Forecast project global id, linked to the ticket as it is created.
	projectId?: string;
}

interface UseCreateTicketResult {
	create: (input: CreateTicketInput, connections?: string[]) => Promise<{id: string; slug: string}>;
	loading: boolean;
	error: string | null;
}

export function useCreateTicket(): UseCreateTicketResult {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	function create(input: CreateTicketInput, connections: string[] = []): Promise<{id: string; slug: string}> {
		setError(null);
		setLoading(true);
		return new Promise((resolve, reject) => {
			commitTicketingMutation(
				CreateTicketMutation,
				{...input, connections},
				response => {
					setLoading(false);
					const node = response.createTicket.ticketEdge.node;
					resolve({id: node.id, slug: node.slug});
				},
				err => {
					setLoading(false);
					const msg = err instanceof Error ? err.message : String(err);
					setError(msg);
					reject(err);
				}
			);
		});
	}

	return {create, loading, error};
}
